// AdminHome — the admin landing page ("Hem"), same shape as EmployerHome and
// CandidateHome: an ordered rule array fed to <HomeActionStack>, plus an
// identity card in the right rail.
//
// #358 replaced "Översikt", which showed four counters (organisations, units,
// employers, active-of-each) that nobody could act on and that cost three
// unbounded table scans per page load. Deliberately NO stat strip here: the
// employer's strip survives because its three numbers are links into filtered
// work queues, whereas "how many organisations exist" is answered by opening
// Organisationer. Useful admin stats can be added once real use shows what they
// are — inventing them to fill the space is what got us here.
//
// The queue is only things an admin can DO something about:
//   1. new contact requests — someone asked to be contacted, nobody has replied
//   2. employer accounts that never got used — created, mailed, never activated
//      (falls out of #355; the fix is one click)
// Everything else on this page is orientation, not work.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Building2, Plus, Lightbulb, ArrowRight, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';
import { adminApi, type PendingEmployerInvitation } from '@/lib/api/adminApi';
import type { Contact } from '@/lib/models/contact';
import { HomeActionStack, type HomeAction } from '@/components/custom-ui/HomeActionStack';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import { OrgLogo } from '@/components/custom-ui/OrgLogo';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW } from '@/lib/nexilink-card-styles';
import { useToast } from '@/hooks/use-toast';
import AdminProfileSummary from './AdminProfileSummary';

const AdminHome: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { organisations } = useAdmin();
    const { toast } = useToast();

    const [newContacts, setNewContacts] = useState<Contact[]>([]);
    const [pendingInvites, setPendingInvites] = useState<PendingEmployerInvitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [resending, setResending] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [contacts, invites] = await Promise.all([
                adminApi.listContacts('new').catch(() => [] as Contact[]),
                adminApi.listPendingEmployerInvitations().catch(() => [] as PendingEmployerInvitation[]),
            ]);
            if (cancelled) return;
            setNewContacts(contacts);
            setPendingInvites(invites);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const handleResend = async (inv: PendingEmployerInvitation) => {
        setResending(inv.id);
        try {
            await adminApi.resendEmployerInvitation(inv.id);
            // The row stays — the person still hasn't activated. Only the expiry
            // moved, so re-fetch rather than optimistically removing them.
            const fresh = await adminApi.listPendingEmployerInvitations().catch(() => pendingInvites);
            setPendingInvites(fresh);
            toast({ title: 'Inbjudan skickad igen', description: `Ett nytt mejl har gått till ${inv.email}.` });
        } catch {
            toast({ title: 'Kunde inte skicka', description: 'Försök igen om en stund.', variant: 'destructive' });
        } finally {
            setResending(null);
        }
    };

    const firstName = user?.first_name || 'där';
    const expiredCount = pendingInvites.filter((i) => i.is_expired).length;

    const actions: HomeAction[] = [
        {
            key: 'new-contacts',
            when: newContacts.length > 0,
            icon: Mail,
            tone: 'amber',
            title: newContacts.length === 1 ? 'En ny kontaktförfrågan' : `${newContacts.length} nya kontaktförfrågningar`,
            description: 'Någon har hört av sig och väntar på svar.',
            ctaLabel: 'Läs förfrågningar',
            ctaIcon: ArrowRight,
            onClick: () => navigate('/dashboard/contacts'),
        },
        {
            key: 'expired-invites',
            when: expiredCount > 0,
            icon: Send,
            tone: 'red',
            title: expiredCount === 1 ? 'En inbjudan har gått ut' : `${expiredCount} inbjudningar har gått ut`,
            description: 'Arbetsgivare som inte hann välja lösenord. Skicka en ny länk.',
            ctaLabel: 'Visa nedan',
            onClick: () => document.getElementById('pending-invites')?.scrollIntoView({ behavior: 'smooth' }),
        },
        {
            key: 'no-orgs',
            when: !loading && organisations.length === 0,
            icon: Building2,
            tone: 'brand',
            title: 'Lägg upp din första organisation',
            description: 'En organisation är kunden. Enheter och arbetsgivare skapas inuti den.',
            ctaLabel: 'Ny organisation',
            ctaIcon: Plus,
            onClick: () => navigate('/dashboard/organisations/new'),
        },
    ];

    const allGood: HomeAction = {
        key: 'all-good',
        when: true,
        icon: Building2,
        tone: 'green',
        title: 'Inget väntar på dig',
        description: 'Inga obesvarade kontaktförfrågningar och alla arbetsgivare har kommit igång.',
        ctaLabel: 'Visa organisationer',
        ctaIcon: ArrowRight,
        onClick: () => navigate('/dashboard/organisations'),
    };

    const recentOrgs = organisations.slice(0, 4);

    return (
        <div className="space-y-6">
            <ExplainerCard id="admin-home" icon={Lightbulb} tone="amber" title="Så funkar startsidan">
                <p className="text-sm text-gray-600 mt-0.5">
                    Här samlas det som väntar på dig — <strong>kontaktförfrågningar att besvara</strong> och{' '}
                    <strong>arbetsgivare som inte kommit igång</strong>. Allt annat hittar du i menyn till vänster.
                </p>
            </ExplainerCard>

            <div>
                <h2 className="text-xl font-bold text-gray-900">Hej {firstName}!</h2>
                <p className="text-sm text-gray-500">
                    {loading ? 'Hämtar…' : 'Det här behöver din uppmärksamhet.'}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-6">
                    {loading ? (
                        <div className="space-y-3">
                            <div className="h-24 rounded-2xl bg-gray-100 animate-pulse" />
                            <div className="h-20 rounded-2xl bg-gray-50 animate-pulse" />
                        </div>
                    ) : (
                        <HomeActionStack actions={actions} fallback={allGood} />
                    )}

                    {/* Arbetsgivare som inte kommit igång — a worklist, not a statistic:
                        every row is a person who cannot sign in, with the fix beside it. */}
                    {!loading && pendingInvites.length > 0 && (
                        <div id="pending-invites" className={NEXILINK_CARD_WHITE_ELEVATED}>
                            <div className="p-5 pb-2">
                                <h3 className="text-sm font-semibold text-gray-900">Har inte kommit igång</h3>
                                <p className="text-sm text-gray-500">
                                    Kontot är skapat men personen har inte valt lösenord än.
                                </p>
                            </div>
                            <div className="px-2 pb-2 divide-y divide-gray-50">
                                {pendingInvites.map((inv) => (
                                    <div key={inv.id} className={NEXILINK_ROW}>
                                        <OrgLogo name={inv.organisation_name} size="md" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{inv.email}</p>
                                            <p className="text-sm text-gray-500 truncate">
                                                {[inv.organisation_name, inv.unit_name].filter(Boolean).join(' — ') || '—'}
                                            </p>
                                        </div>
                                        {inv.is_expired && (
                                            <span className="text-xs font-medium text-red-600 flex-shrink-0">Utgången</span>
                                        )}
                                        <button
                                            onClick={() => handleResend(inv)}
                                            disabled={resending === inv.id}
                                            className="flex-shrink-0 text-xs font-medium text-[#1A4D4F] hover:underline disabled:opacity-50"
                                        >
                                            {resending === inv.id ? 'Skickar…' : 'Skicka igen'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Senaste organisationer — orientation, and the way back in. */}
                    {!loading && recentOrgs.length > 0 && (
                        <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                            <div className="p-5 pb-2 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-gray-900">Senaste organisationer</h3>
                                <button
                                    onClick={() => navigate('/dashboard/organisations')}
                                    className="text-xs font-medium text-[#1A4D4F] hover:underline"
                                >
                                    Visa alla
                                </button>
                            </div>
                            <div className="px-2 pb-2 divide-y divide-gray-50">
                                {recentOrgs.map((org) => (
                                    <div
                                        key={org.id}
                                        className={`${NEXILINK_ROW} cursor-pointer`}
                                        onClick={() => navigate(`/dashboard/organisations/${org.id}`)}
                                    >
                                        <OrgLogo name={org.name} logoUrl={org.logo_url} size="md" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{org.name}</p>
                                            <p className="text-sm text-gray-500 truncate">
                                                {org.units_count} enheter · {org.employers_count} arbetsgivare
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-1">
                    <AdminProfileSummary />
                </div>
            </div>
        </div>
    );
};

export default AdminHome;
